/**
 * 🇨🇴 DIAN Service - Servicio de Facturación Electrónica Colombia
 * 
 * Maneja todo el proceso de:
 * - Parseo de documentos a formato DIAN
 * - Envío a plataforma DIAN
 * - Procesamiento de respuestas
 * 
 * Tipos de documento soportados:
 * - FE: Factura Electrónica
 * - FC: Factura de Contingencia  
 * - NC: Nota Crédito
 */

import { resolveDianDocTypeId } from "@/utils/dianDocTypes"

// URL del webservice DIAN (reemplazar por la URL real)
// URL del webservice DIAN (URL real configurada)
const DIAN_WS_URL = "https://ai-bos.xyz/erp/ws/fe_ws/"

/**
 * Webservice específico para DIAN
 */
const fe_ws = (method, data, callback) => {
  console.log('🇨🇴 DIAN WS - Método:', method)
  console.log('📤 DIAN WS - Data:', data)
  
  const wsUrl = DIAN_WS_URL + method
  const strData = JSON.stringify(data)
  
  const options = {
    method: 'POST',
    body: strData,
    headers: {
      'Content-Type': 'application/json'
    }
  }
  
  console.log('🔗 DIAN WS - URL:', wsUrl)
  console.log('📨 DIAN WS - Enviando request...')
  
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    console.log('⏰ DIAN WS - TIMEOUT tras 60 segundos')
    controller.abort()
  }, 60000)
  
  fetch(wsUrl, {
    ...options,
    signal: controller.signal
  })
  .then(response => {
    clearTimeout(timeoutId)
    console.log('📥 DIAN WS - Response status:', response.status)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return response.json()
  })
  .then(data => {
    console.log('✅ DIAN WS - Response data:', data)
    callback(data)
  })
  .catch(error => {
    clearTimeout(timeoutId)
    console.error('❌ DIAN WS - Error:', error)
    callback({ 
      error: true, 
      message: error.message,
      data: { errors: [{ message: error.message }] }
    })
  })
}

/**
 * 🇨🇴 Servicio Principal DIAN
 */
export class DIANService {
  
  /**
   * Procesar documento para DIAN
   * @param {Object} documentData - Datos del documento desde doc_viewer
   * @param {Function} callback - Callback con resultado
   */
  static async processDocument(documentData, callback) {
    try {
      console.log('🇨🇴 DIANService: Iniciando procesamiento')
      console.log('📋 Documento recibido:', documentData)
      
      const docType = documentData.document?.doc_type
      
      // Validar que es documento colombiano
      if (!this.isColombiaNDocument(documentData)) {
        throw new Error('Documento no es de Colombia o no requiere procesamiento DIAN')
      }
      
      // Procesar según tipo de documento
      switch (docType) {
        case 'FE':
          this.processFE(documentData, callback)
          break
        case 'FC': 
          this.processFC(documentData, callback)
          break
        case 'NC':
          this.processNC(documentData, callback)
          break
        default:
          throw new Error(`Tipo de documento no soportado: ${docType}`)
      }
      
    } catch (error) {
      console.error('❌ DIANService Error:', error)
      callback({
        success: false,
        error: error.message,
        data: { errors: [{ message: error.message }] }
      })
    }
  }
  
  /**
   * Verificar si es documento de Colombia
   */
  static isColombiaNDocument(data) {
    // 1. Verificar por CUFE
    if (data.document?.cufe || data.document?.cufe_nc) {
      return true
    }
    
    // 2. Verificar estructura DIAN
    if (data.permit?.InvoiceAuthorization || data.seller?.TaxLevelCode) {
      return true
    }
    
    // 3. Verificar environment DIAN
    if (data.company?.environment) {
      return true
    }
    
    // 4. Verificar país del seller
    if (data.seller?.country_code === 'CO') {
      return true
    }
    
    return false
  }
  
  /**
   * Procesar Factura Electrónica (FE)
   */
  static processFE(documentData, callback) {
    console.log('📋 Procesando Factura Electrónica (FE)')
    
    try {
      // Parsear a formato DIAN
      const dianFormat = this.parseToDANFormat(documentData)
      
      // Enviar a DIAN
      this.sendToDIAN('post_invoice', dianFormat, documentData, callback)
      
    } catch (error) {
      console.error('❌ Error procesando FE:', error)
      callback({
        success: false,
        error: error.message,
        data: { errors: [{ message: error.message }] }
      })
    }
  }
  
  /**
   * Procesar Factura de Contingencia (FC)
   */
  static processFC(documentData, callback) {
    console.log('📋 Procesando Factura de Contingencia (FC)')
    
    try {
      // Parsear a formato DIAN
      const dianFormat = this.parseToDANFormat(documentData)
      
      // Enviar a DIAN como contingencia
      this.sendToDIAN('post_invoice_c', dianFormat, documentData, callback)
      
    } catch (error) {
      console.error('❌ Error procesando FC:', error)
      callback({
        success: false,
        error: error.message,
        data: { errors: [{ message: error.message }] }
      })
    }
  }
  
  /**
   * Procesar Nota de Crédito (NC)
   */
  static processNC(documentData, callback) {
    console.log('📋 Procesando Nota de Crédito (NC)')
    
    try {
      // Parsear a formato DIAN
      const dianFormat = this.parseToDANFormat(documentData)
      
      // Enviar a DIAN
      this.sendToDIAN('post_credit_note', dianFormat, documentData, callback)
      
    } catch (error) {
      console.error('❌ Error procesando NC:', error)
      callback({
        success: false,
        error: error.message,
        data: { errors: [{ message: error.message }] }
      })
    }
  }
  
  /**
   * Parsear documento a formato DIAN
   */
  static parseToDANFormat(documentData) {
    // console.log('🔄 Parseando a formato DIAN...')
    console.log('📋 Estructura completa recibida:', JSON.stringify(documentData, null, 2))
    
    const { document: doc, items, buyer, company, seller } = documentData
    
    // Log detallado para debugging
    // console.log('📄 Document:', doc)
    // console.log('🏢 Company:', company) 
    // console.log('👤 Buyer:', buyer)
    // console.log('🏪 Seller:', seller)
    // console.log('📦 Items:', items)
    
    // Validaciones de datos requeridos
    if (!doc) {
      throw new Error('No se encontraron datos del documento')
    }
    
    if (!buyer) {
      throw new Error('No se encontraron datos del comprador')
    }
    
    if (!company && !seller) {
      throw new Error('No se encontraron datos de la empresa')
    }
    
    // Usar company o seller como fallback
    const companyData = company || seller || {}
    const buyerData = buyer || {}

    console.log('🏢 Datos de empresa:', companyData );
    
    // 🇨🇴 LÓGICA EXACTA DE COLOMBIA PARA TIPOS DE DOCUMENTO
    // 
    // Códigos DIAN oficiales:
    // "13" = Cédula de ciudadanía (CC) -> type_organization_id: 2 (Persona Natural)
    // "31" = NIT -> type_organization_id: 1 (Persona Jurídica) 
    // "6"  = NIT (código alternativo) -> type_organization_id: 1 (Persona Jurídica)
    //
    // IMPORTANTE: En el sistema original se usa "6" para determinar persona jurídica
    // pero en los arrays de configuración aparece "31" para NIT
    
    let type_organization = 2 // Por defecto persona natural
    const partyTypeId = String(buyerData.PartyTypeId || '').trim()
    
    console.log('🆔 PartyTypeId recibido:', partyTypeId, typeof partyTypeId)
    
    // Lógica exacta del original: "6" = NIT = Persona Jurídica
    if (partyTypeId === "6" || partyTypeId === "31") {
      type_organization = 1 // Persona jurídica
      console.log('✅ Detectado como Persona Jurídica (NIT)')
    } else {
      type_organization = 2 // Persona natural
      console.log('✅ Detectado como Persona Natural (CC u otro)')
    }
    
    // ✅ VALIDACIONES CRÍTICAS PARA DIAN
    // Mapear municipality_id desde diferentes fuentes posibles (como en original)
    let branchMunicipalityId = documentData.company?.city_code || 
                                companyData.city_code || 
                                companyData.municipality_id || 
                                seller?.city_code
    
    // Convertir branchMunicipalityId a entero
    branchMunicipalityId = parseInt(branchMunicipalityId) || 149; // 149 = Bogotá por defecto
                                
    let customerMunicipalityId = documentData.buyer?.municipality_id || 
                                  companyData.municipality_id || 
                                  buyer?.municipality_id
    
    // Si customerMunicipalityId no es válido, usar branchMunicipalityId como respaldo
    if (!customerMunicipalityId || customerMunicipalityId === '' || customerMunicipalityId === null) {
      customerMunicipalityId = branchMunicipalityId;
      console.log('⚠️ customerMunicipalityId no válido, usando branchMunicipalityId:', branchMunicipalityId);
    } else {
      // Convertir customerMunicipalityId a entero
      customerMunicipalityId = parseInt(customerMunicipalityId) || branchMunicipalityId;
    }
    
    // console.log('🏢 Búsqueda municipality_id para branch_office:')
    // console.log('  companyData.city_code:', companyData.city_code)
    // console.log('  companyData.municipality_id:', companyData.municipality_id)
    // console.log('  seller?.city_code:', seller?.city_code)
    // console.log('  → branchMunicipalityId final:', branchMunicipalityId)
    
    // console.log('👤 Búsqueda municipality_id para customer:')
    // console.log('  buyerData.municipality_id:', buyerData.municipality_id)
    // console.log('  buyer?.municipality_id:', buyer?.municipality_id)
    // console.log('  → customerMunicipalityId final:', customerMunicipalityId)
    
    if (!branchMunicipalityId) {
      throw new Error('Falta municipality_id para branch_office. Revisar: company.city_code, company.municipality_id, seller.city_code')
    }
    
    if (!customerMunicipalityId) {
      throw new Error('Falta municipality_id para customer. Revisar: buyer.municipality_id')
    }
    
    // Verificar sync_code (lógica exacta del original)
    const syncCode = companyData.sync_code === "1" || companyData.sync_code === 1
    console.log('🔄 Sync code:', syncCode)
    
    // 📋 ESTRUCTURA DIAN EXACTA DEL ORIGINAL
    const dianFormat = {
      number: doc.invoice ? parseInt(doc.invoice.split("-")[1]) : parseInt(doc.doc_number), // ✅ CONVERTIR A ENTERO
      sync: syncCode,
      type_document_id: 1,
      
      branch_office: {
        name: companyData.PartyName,
        phone: companyData.client_phone,
        address: companyData.AddressLine,
        trade_name: companyData.PartyName,
        municipality_id: branchMunicipalityId, // ✅ USAR VALOR VALIDADO
      },
      
      customer: {
        // El API DIAN espera el id del catálogo (entero), no el code ni el código local.
        // Ver src/utils/dianDocTypes.js para el mapeo y los fallbacks.
        type_document_identification_id: resolveDianDocTypeId(partyTypeId, type_organization),
        identification_number: buyerData.PartyIdentification,
        type_organization_id: type_organization, // ✅ Calculado correctamente
        name: buyerData.PartyName,
        address: buyerData.AddressLine,
        municipality_id: customerMunicipalityId, // ✅ USAR VALOR VALIDADO
        email: buyerData.PartyEmail,
      },
      
      legal_monetary_totals: {
        line_extension_amount: 0,
        tax_exclusive_amount: 0,
        tax_inclusive_amount: 0,
        allowance_total_amount: 0,
        charge_total_amount: 0,
        payable_amount: 0
      },
      
      invoice_lines: []
    }
    
    // 📦 PROCESAMIENTO DE ITEMS - SOPORTA IVA VARIABLE POR ITEM
    
    if (items && Array.isArray(items)) {
      console.log('📦 Procesando', items.length, 'items...')
      
      // Primer paso: procesar precios con IVA variable
      items.forEach((item, index) => {
        console.log(`📦 Item ${index + 1}:`, item)
        
        item.item_quantity = parseFloat(item.item_quantity || item.quantity || 1)
        item.item_price = parseFloat(item.item_price || item.price || 0)
        item.item_discount = parseFloat(item.item_discount || item.discount || 0)
        item.item_tax = parseFloat(item.item_tax || item.tax || 0)
        
        // Calcular tasa de IVA individual para cada item
        let itemTaxRate = 0; // Por defecto 0 (no forzar IVA)
        
        if (item.item_tax > 0) {
          // Si el item trae su propio impuesto, calcular el porcentaje
          let basePrice = item.item_price - item.item_tax
          itemTaxRate = (item.item_tax / basePrice) * 100
          console.log(`📊 IVA calculado para item ${index + 1}: ${itemTaxRate.toFixed(2)}%`)
        } else {
          // Si no hay impuesto específico, mantener en 0 (no aplicar IVA por defecto)
          itemTaxRate = 0
          item.item_tax = 0 // Asegurar que se mantenga en 0
          console.log(`📊 Item ${index + 1} exento de IVA (tax=0)`)
        }
        
        // Guardar la tasa de impuesto para uso posterior
        item.taxRate = itemTaxRate
        
        // NO redondear aquí - calcular precio sin IVA manteniendo precisión
        item.price_amount = item.item_price / (1 + itemTaxRate / 100)
        
        // Calcular extensión de línea sin redondear
        item.line_extension_amount = item.price_amount * item.item_quantity
        
        // Calcular impuesto sin redondear
        item.tax = (item.line_extension_amount * itemTaxRate) / 100
        
        console.log(`📊 Item procesado ${index + 1}:`, {
          quantity: item.item_quantity,
          price_with_tax: item.item_price,
          price_without_tax: item.price_amount,
          tax_rate: itemTaxRate.toFixed(2) + '%',
          tax: item.tax,
          line_extension: item.line_extension_amount
        })
      })
      
      // Segundo paso: crear invoice_lines con redondeo correcto
      items.forEach((item, index) => {
        // CRÍTICO: Redondear line_extension_amount PRIMERO
        const roundedLineExtension = Math.round(item.line_extension_amount)
        const roundedPriceAmount = Math.round(item.price_amount)
        const roundedDiscount = Math.round(item.item_discount)
        const taxRate = Math.round(item.taxRate || 0)
        
        // LUEGO calcular tax sobre el line_extension_amount REDONDEADO
        const roundedTax = Math.round((roundedLineExtension * taxRate) / 100)
        
        const itm = {
          description: item.item_concept || item.description || 'Producto',
          unit_measure_id: 938, // Código exacto del original
          code: item.item_code || item.code || `ITEM${index + 1}`,
          type_item_identification_id: 3, // Código exacto del original
          base_quantity: item.item_quantity.toFixed(6),
          invoiced_quantity: item.item_quantity.toFixed(6),
          price_amount: roundedPriceAmount,
          line_extension_amount: roundedLineExtension,
        }
        
        // Lógica de precio cero (exacta del original)
        if (roundedLineExtension == 0) {
          itm.reference_price_id = 1
          itm.price_amount = roundedDiscount
        }
        
        // Lógica de descuentos (exacta del original)
        if (item.item_discount > 0) {
          const discountAmount = item.item_discount * item.item_quantity
          const baseAmount = (item.price_amount + item.item_discount) * item.item_quantity
          itm.allowance_charges = [
            {
              charge_indicator: false,
              allowance_charge_reason: "Discount",
              amount: Math.round(discountAmount),
              base_amount: Math.round(baseAmount),
            },
          ]
          dianFormat.legal_monetary_totals.allowance_total_amount += Math.round(discountAmount)
        }
        
        // Tax totals con IVA variable - Solo incluir si hay impuesto
        if (roundedTax > 0) {
          // Determinar tax_id basado en el porcentaje de IVA
          const taxId = taxRate === 8 ? 4 : 1 // Si es 8% usa tax_id 4, sino usa tax_id 1
          
          itm.tax_totals = [
            {
              tax_id: taxId,
              tax_amount: roundedTax,
              taxable_amount: roundedLineExtension,
              percent: taxRate,
            },
          ]
        }
        
        // Acumular totales
        dianFormat.legal_monetary_totals.line_extension_amount += roundedLineExtension
        if (roundedTax > 0) {
          dianFormat.legal_monetary_totals.tax_exclusive_amount += roundedLineExtension
        }
        dianFormat.legal_monetary_totals.tax_inclusive_amount += roundedLineExtension + roundedTax
        dianFormat.legal_monetary_totals.charge_total_amount += roundedLineExtension + roundedTax
        dianFormat.legal_monetary_totals.payable_amount += roundedLineExtension + roundedTax
        
        dianFormat.invoice_lines.push(itm)
      })
      
      // 💰 PROCESAMIENTO DE PAGOS - BOLSAS ICB (como en el original)
      if (documentData.payments && Array.isArray(documentData.payments)) {
        const bags = documentData.payments.find(
          (item) => item.payment_concept === "ICB ley 1819 de 2016"
        )
        
        if (bags && typeof bags === "object" && parseFloat(bags.payment_value) > 0) {
          console.log('💰 Procesando ICB Bolsas:', bags)
          
          bags.quantity = parseFloat(bags.payment_value) / 70
          bags.tax = 70
          
          const itmBags = {
            description: "Bolsas",
            unit_measure_id: 886,
            code: "000000000000",
            type_item_identification_id: 3,
            base_quantity: parseFloat("1.000000"), // ✅ CONVERTIR A FLOAT
            invoiced_quantity: parseFloat(bags.quantity.toFixed(6)), // ✅ CONVERTIR A FLOAT
            price_amount: parseFloat(parseFloat(bags.payment_value).toFixed(2)), // ✅ CONVERTIR A FLOAT
            line_extension_amount: parseFloat("0.00"), // ✅ CONVERTIR A FLOAT
            reference_price_id: 1,
            tax_totals: [
              {
                tax_id: 10,
                tax_amount: parseFloat(parseFloat(bags.payment_value).toFixed(2)), // ✅ CONVERTIR A FLOAT
                taxable_amount: parseFloat(parseFloat(bags.quantity).toFixed(2)), // ✅ CONVERTIR A FLOAT
                unit_measure_id: 886,
                per_unit_amount: parseFloat(bags.tax.toFixed(2)), // ✅ CONVERTIR A FLOAT
                base_unit_measure: parseFloat(bags.quantity.toFixed(6)), // ✅ CONVERTIR A FLOAT
              },
            ],
          }
          
          // Acumular totales de bolsas (exacto del original)
          dianFormat.legal_monetary_totals.line_extension_amount += parseFloat(itmBags.line_extension_amount)
          dianFormat.legal_monetary_totals.tax_exclusive_amount += parseFloat(itmBags.tax_totals[0].taxable_amount)
          dianFormat.legal_monetary_totals.tax_inclusive_amount += 
            parseFloat(itmBags.line_extension_amount) + parseFloat(bags.payment_value)
          dianFormat.legal_monetary_totals.payable_amount += 
            parseFloat(itmBags.line_extension_amount) + parseFloat(bags.payment_value)
          
          dianFormat.invoice_lines.push(itmBags)
        }
      }
      
      // 📊 FORMATEAR TOTALES FINALES (exacto del original)
      dianFormat.legal_monetary_totals.line_extension_amount = 
        dianFormat.legal_monetary_totals.line_extension_amount.toFixed(2)
      dianFormat.legal_monetary_totals.tax_exclusive_amount = 
        dianFormat.legal_monetary_totals.tax_exclusive_amount.toFixed(2)
      dianFormat.legal_monetary_totals.tax_inclusive_amount = 
        dianFormat.legal_monetary_totals.tax_inclusive_amount.toFixed(2)
      dianFormat.legal_monetary_totals.allowance_total_amount = 
        dianFormat.legal_monetary_totals.allowance_total_amount.toFixed(2)
      dianFormat.legal_monetary_totals.charge_total_amount = 
        dianFormat.legal_monetary_totals.charge_total_amount.toFixed(2)
      dianFormat.legal_monetary_totals.payable_amount = 
        dianFormat.legal_monetary_totals.payable_amount.toFixed(2)
    }
    
    console.log('✅ Formato DIAN generado (exacto del original):', dianFormat)
    return dianFormat
  }
  
  /**
   * Enviar a plataforma DIAN
   */
  static sendToDIAN(method, dianData, originalData, callback) {
    console.log('🚀 Enviando a plataforma DIAN...')
    
    const query = {
      method: "POST",
      nit: originalData.seller?.PartyIdentification || originalData.company?.PartyIdentification,
      environment: parseInt(originalData.company?.environment || 2),
      data: dianData,
      store: originalData.document.store,
      issueDate: originalData.document.issuedate,
      did: originalData.document.doc_number,
    }
    
    // Agregar resolución para contingencia
    if (method === 'post_invoice' && originalData.permit) {
      query.resolution = originalData.permit
    }
    
    console.log('📤 Query DIAN:', query)
    
    fe_ws(method, query, (response) => {
      console.log('📥 Respuesta DIAN:', response)
      
      if (response.error) {
        callback({
          success: false,
          error: response.message,
          data: response.data
        })
        return
      }
      
      if (response.data && response.data.errors) {
        callback({
          success: false,
          error: 'Error DIAN',
          data: response.data,
          dian_response: response.data
        })
        return
      }
      
      // Éxito
      callback({
        success: true,
        cufe: response.cufe,
        data: response.data,
        document_updated: {
          ...originalData.document,
          cufe: response.cufe
        }
      })
    })
  }
  
  /**
   * Reenviar documento a DIAN (para botones de reenvío)
   */
  static resendDocument(documentData, callback) {
    console.log('🔄 Reenviando documento a DIAN...')
    this.processDocument(documentData, callback)
  }
}

export default DIANService
